/**
 * The alarms of the vendor ops view (D27): the conditions holding on the
 * latest status — server offline during a live production, standby missing,
 * certificate expiring, clock drift, device disconnected on air — worst
 * first, each with its tenant, site and subject.
 */
import type { AlarmWire } from '@acp/api-contract/vendor.js';
import { BellOffIcon } from 'lucide-react';
import { alarmTone, sortAlarms } from '../../lib/vendor.js';
import { Badge } from '../ui/badge.js';
import { UNKNOWN_LABEL } from '../../lib/openEnum.js';

export type AlarmListProps = {
  readonly alarms: readonly AlarmWire[];
  /** Show the tenant column (the cross-tenant view); a tenant's own detail page hides it. */
  readonly showTenant: boolean;
};

const CONDITION_LABEL: Readonly<Record<AlarmWire['type'], string>> = {
  'server-offline-live': 'Server offline (live)',
  'standby-missing': 'Standby missing',
  'cert-expiring': 'Certificate expiring',
  'clock-drift': 'Clock drift',
  'device-disconnected-on-air': 'Device disconnected on air',
  'standby-out-of-sync': 'Standby out of sync',
  'production-running-long': 'Production running 7+ days',
  // A condition a newer cloud evaluates (D37).
  unknown: UNKNOWN_LABEL,
};

export function AlarmList({ alarms, showTenant }: AlarmListProps) {
  if (alarms.length === 0) {
    return (
      <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground" data-testid="alarms-none">
        <BellOffIcon className="size-4" aria-hidden="true" />
        No alarms: every condition is clear on the latest status.
      </p>
    );
  }
  return (
    <ul className="grid gap-2" aria-label="Alarms" data-testid="alarm-list">
      {sortAlarms(alarms).map((alarm) => (
        <li key={alarm.key} className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm" data-testid="alarm" data-severity={alarm.severity} data-type={alarm.type}>
          <Badge variant={alarmTone(alarm.severity)}>{alarm.severity}</Badge>
          <span className="font-medium">{CONDITION_LABEL[alarm.type]}</span>
          <span className="min-w-0 flex-1 text-muted-foreground">{alarm.summary}</span>
          <span className="font-mono text-xs text-muted-foreground">
            {showTenant ? `${alarm.tenantId} · ` : ''}
            {alarm.siteId} · {alarm.subject}
          </span>
        </li>
      ))}
    </ul>
  );
}
